import { QuantityLabel, SANStorageType } from './enums'
import { ProjectType, SiteType, SiteDesignType } from './types'

export const formatMainDataDates = <T extends ProjectType | SiteType | SiteDesignType>(mainDataDates: T[]): T[] => {
  mainDataDates.forEach(p => {
    p.formattedCreatedUtc = toShortDate(p.createdDate)
    p.formattedUpdatedUtc = toShortDate(p.updatedDate)
  })
  return mainDataDates
}

export const toShortDate = (dateString: string): string => {
  if (!dateString) return ''

  const date = new Date(dateString)
  const dd = String(date.getDate()).padStart(2, '0')
  const mm = String(date.getMonth() + 1).padStart(2, '0')
  const yyyy = date.getFullYear()
  return `${dd}/${mm}/${yyyy}`
}

export const enumToOptionList = <EnumType>(enumType: EnumType): { label: string; value: string }[] =>
  Object.values(enumType)
    .filter(value => !value.includes(':hidden'))
    .map(key => ({ label: key, value: key }))

export const toCamelCase = (inputName: string): string => {
  return inputName.toLowerCase().replace(/\s+(\w)/g, (_match, p1) => p1.toUpperCase())
}

export const isNameLengthTooLong = (name: string, maxLength: number): boolean => {
  return name.length > maxLength
}

export const getQuantityLabel = (connectivity: string): QuantityLabel => {
  const isSANConnectivity = (_connectivity?: string): boolean => {
    return _connectivity === SANStorageType.iSCSISAN || _connectivity === SANStorageType.FibreChannelSAN
  }

  return isSANConnectivity(connectivity) ? QuantityLabel.Quantity : QuantityLabel.QuantityPerServer
}

export const downloadBlob = (blob: Blob, filename: string): void => {
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(url)
}

export const toSafeFileName = (name: string, fallback: string): string => {
  const trimmedName = name?.trim()
  if (!trimmedName) return fallback

  return trimmedName.replace(/[\\/:*?"<>|]/g, '_')
}

//The site design export endpoint labels its body application/json but encodes it with
//Encoding.Unicode (UTF-16 LE without a BOM), so the encoding has to be read from the bytes
//instead of the header. Sniffing rather than assuming UTF-16 keeps this working if the
//backend later switches to Encoding.UTF8.
const sniffTextEncodingLabel = (bytes: Uint8Array): string => {
  if (bytes[0] === 0xff && bytes[1] === 0xfe) return 'utf-16le'
  if (bytes[0] === 0xfe && bytes[1] === 0xff) return 'utf-16be'

  //Without a BOM: JSON always starts with an ASCII character, so a null byte in the second
  //position means UTF-16 LE and a null byte in the first means UTF-16 BE.
  if (bytes.length > 1 && bytes[0] !== 0 && bytes[1] === 0) return 'utf-16le'
  if (bytes.length > 1 && bytes[0] === 0 && bytes[1] !== 0) return 'utf-16be'

  return 'utf-8'
}

export const decodeJsonBytes = (buffer: ArrayBuffer): string => {
  const bytes = new Uint8Array(buffer)
  if (bytes.length === 0) return ''

  //TextDecoder strips a leading BOM for all three labels, so only the label has to be picked.
  return new TextDecoder(sniffTextEncodingLabel(bytes)).decode(bytes)
}

//Mirrors the FileDownloadName the backend sets on the export response. We rebuild it client
//side because the API is on a different origin than the client in every environment, so the
//browser hides Content-Disposition unless the response also sends Access-Control-Expose-Headers.
export const buildSiteDesignExportFileName = (siteDesignName: string, siteDesignId: number, now: Date = new Date()): string => {
  const pad = (value: number): string => String(value).padStart(2, '0')
  const timestamp = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`

  return `${toSafeFileName(siteDesignName, `SiteDesign_${siteDesignId}`)}_XSD-Export_${timestamp}.json`
}
